import { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useShelters } from "@/hooks/useShelters";
import { parseEdgeFunctionErrorAsync } from "@/lib/parseEdgeFunctionError";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Send, ArrowLeft, CheckCircle, Heart, AlertTriangle, BadgeCheck } from "lucide-react";
import { toast } from "sonner";
import { z } from "zod";
import { PageBreadcrumbs } from "@/components/PageBreadcrumbs";

const updateSchema = z.object({
  current_needs: z.string().trim().max(1000, "Potrzeby mogą mieć max. 1000 znaków").optional(),
  volunteer_info: z.string().trim().max(1000, "Info o wolontariacie może mieć max. 1000 znaków").optional(),
});

const QuickUpdatePage = () => {
  const { data: shelters, isLoading: sheltersLoading } = useShelters();
  const [selectedShelterId, setSelectedShelterId] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const [formData, setFormData] = useState({
    current_needs: "",
    urgent_needs: false,
    dog_walking_available: false,
    dogs_available: true,
    cats_available: true,
    volunteer_info: "",
    is_organization_request: false,
  });

  // Load current shelter data when selected
  useEffect(() => {
    if (selectedShelterId && shelters) {
      const shelter = shelters.find((s) => s.id === selectedShelterId);
      if (shelter) {
        setFormData({
          current_needs: shelter.current_needs || "",
          urgent_needs: shelter.urgent_needs || false,
          dog_walking_available: shelter.dog_walking_available || false,
          dogs_available: shelter.dogs_available ?? true,
          cats_available: shelter.cats_available ?? true,
          volunteer_info: shelter.volunteer_info || "",
          is_organization_request: false,
        });
      }
    }
  }, [selectedShelterId, shelters]);

  const handleChange = (field: keyof typeof formData, value: string | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    if (!selectedShelterId) {
      toast.error("Wybierz schronisko");
      return;
    }

    const shelter = shelters?.find((s) => s.id === selectedShelterId);
    if (!shelter) {
      toast.error("Nie znaleziono schroniska");
      return;
    }

    // Validate
    const dataToValidate = {
      current_needs: formData.current_needs || undefined,
      volunteer_info: formData.volunteer_info || undefined,
    };

    const result = updateSchema.safeParse(dataToValidate);

    if (!result.success) {
      const fieldErrors: Record<string, string> = {};
      result.error.errors.forEach((err) => {
        if (err.path[0]) {
          fieldErrors[err.path[0] as string] = err.message;
        }
      });
      setErrors(fieldErrors);
      toast.error("Popraw błędy w formularzu");
      return;
    }

    setIsSubmitting(true);

    try {
      const { data, error, response } = await supabase.functions.invoke("submit-shelter-update", {
        body: {
          shelter_id: selectedShelterId,
          shelter_name: shelter.name,
          city: shelter.city,
          region: shelter.region,
          address: shelter.address,
          postal_code: shelter.postal_code,
          lat: shelter.lat,
          lng: shelter.lng,
          phone: shelter.phone,
          email: shelter.email,
          website: shelter.website,
          description: shelter.description,
          current_needs: formData.current_needs.trim() || null,
          urgent_needs: formData.urgent_needs,
          dog_walking_available: formData.dog_walking_available,
          dogs_available: formData.dogs_available,
          cats_available: formData.cats_available,
          volunteer_info: formData.volunteer_info.trim() || null,
          is_organization_request: formData.is_organization_request,
        },
      });

      if (error) {
        // Prefer the raw response body from invoke() when available
        if (response) {
          try {
            const text = await response.clone().text();
            const parsed = JSON.parse(text);
            if (parsed?.error) {
              toast.error(String(parsed.error));
              return;
            }
          } catch {
            // ignore
          }
        }

        toast.error(await parseEdgeFunctionErrorAsync(error));
        return;
      }
      if (data?.error) throw new Error(data.error);

      setIsSubmitted(true);
      toast.success("Aktualizacja wysłana!");
    } catch (error: any) {
      toast.error(await parseEdgeFunctionErrorAsync(error));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (sheltersLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-md text-center">
          <CardContent className="py-12">
            <CheckCircle className="w-16 h-16 mx-auto mb-4 text-primary" />
            <h2 className="font-display font-bold text-xl mb-2">Dziękujemy!</h2>
            <p className="text-muted-foreground mb-6">
              Twoja aktualizacja została wysłana i oczekuje na zatwierdzenie przez administratora.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/">
                <Button variant="outline">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Wróć do mapy
                </Button>
              </Link>
              <Button
                onClick={() => {
                  setIsSubmitted(false);
                  setSelectedShelterId("");
                  setFormData({
                    current_needs: "",
                    urgent_needs: false,
                    dog_walking_available: false,
                    dogs_available: true,
                    cats_available: true,
                    volunteer_info: "",
                    is_organization_request: false,
                  });
                }}
              >
                Zgłoś kolejną aktualizację
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background py-8 px-4">
      <Helmet>
        <title>Szybka Aktualizacja - GdzieSchronisko</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <div className="max-w-lg mx-auto">
        <PageBreadcrumbs currentPage="Szybka aktualizacja" />

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Heart className="w-5 h-5" />
              Szybka aktualizacja potrzeb
            </CardTitle>
            <CardDescription>Zaktualizuj informacje o aktualnych potrzebach schroniska.</CardDescription>
            <div className="mt-3 flex items-start gap-2 p-3 bg-accent dark:bg-accent/30 border border-primary/20 rounded-lg">
              <AlertTriangle className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
              <p className="text-sm text-accent-foreground dark:text-primary-foreground/80">
                Zgłoszenie zostanie sprawdzone przez administratora przed publikacją. Może to potrwać do kilku dni
                roboczych.
              </p>
            </div>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Shelter Selection */}
              <div className="space-y-2">
                <Label htmlFor="shelter">Wybierz schronisko *</Label>
                <Select value={selectedShelterId} onValueChange={setSelectedShelterId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Wybierz schronisko..." />
                  </SelectTrigger>
                  <SelectContent>
                    {shelters?.map((shelter) => (
                      <SelectItem key={shelter.id} value={shelter.id}>
                        {shelter.name} ({shelter.city})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {selectedShelterId && (
                <>
                  {/* Current Needs */}
                  <div className="space-y-2">
                    <Label htmlFor="current_needs">Aktualne potrzeby</Label>
                    <Textarea
                      id="current_needs"
                      value={formData.current_needs}
                      onChange={(e) => handleChange("current_needs", e.target.value)}
                      placeholder="Karma dla psów, koce, środki czystości..."
                      rows={4}
                    />
                    {errors.current_needs && <p className="text-xs text-destructive">{errors.current_needs}</p>}
                    <p className="text-xs text-accent-foreground bg-accent dark:bg-accent/30 px-2 py-1.5 rounded">
                      💡 Konkretne informacje (marki, rozmiary) ułatwiają ludziom pomoc.
                    </p>
                  </div>

                  {/* Status Switches */}
                  <div className="space-y-3">
                    <Label>Status</Label>

                    <div className="flex items-center justify-between p-3 border rounded-lg bg-orange-50 dark:bg-orange-950/20">
                      <div className="flex items-center gap-2">
                        <AlertTriangle className="w-4 h-4 text-orange-600" />
                        <Label htmlFor="urgent" className="cursor-pointer font-medium">
                          Pilne potrzeby
                        </Label>
                      </div>
                      <Switch
                        id="urgent"
                        checked={formData.urgent_needs}
                        onCheckedChange={(checked) => handleChange("urgent_needs", checked)}
                      />
                    </div>

                    <div className="flex items-center justify-between p-3 border rounded-lg">
                      <Label htmlFor="walking" className="cursor-pointer">
                        Możliwość spacerów z psami
                      </Label>
                      <Switch
                        id="walking"
                        checked={formData.dog_walking_available}
                        onCheckedChange={(checked) => handleChange("dog_walking_available", checked)}
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div className="flex items-center justify-between p-3 border rounded-lg">
                        <Label htmlFor="dogs" className="cursor-pointer text-sm">
                          Psy do adopcji
                        </Label>
                        <Switch
                          id="dogs"
                          checked={formData.dogs_available}
                          onCheckedChange={(checked) => handleChange("dogs_available", checked)}
                        />
                      </div>
                      <div className="flex items-center justify-between p-3 border rounded-lg">
                        <Label htmlFor="cats" className="cursor-pointer text-sm">
                          Koty do adopcji
                        </Label>
                        <Switch
                          id="cats"
                          checked={formData.cats_available}
                          onCheckedChange={(checked) => handleChange("cats_available", checked)}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Volunteer Info */}
                  <div className="space-y-2">
                    <Label htmlFor="volunteer_info">Informacje o wolontariacie</Label>
                    <Textarea
                      id="volunteer_info"
                      value={formData.volunteer_info}
                      onChange={(e) => handleChange("volunteer_info", e.target.value)}
                      placeholder="Godziny spacerów, jak zostać wolontariuszem..."
                      rows={3}
                    />
                    {errors.volunteer_info && <p className="text-xs text-destructive">{errors.volunteer_info}</p>}
                    <p className="text-xs text-accent-foreground bg-accent dark:bg-accent/30 px-2 py-1.5 rounded">
                      💡 Kilka szczegółów robi różnicę - np. godziny spacerów, dni przyjęć czy sposób zgłoszenia.
                    </p>
                  </div>

                  {/* Organization verification request */}
                  <div
                    className={`flex items-start gap-3 p-4 border rounded-lg cursor-pointer transition-colors ${
                      formData.is_organization_request ? "bg-primary/5 border-primary" : "hover:bg-muted/50"
                    }`}
                    onClick={() => handleChange("is_organization_request", !formData.is_organization_request)}
                  >
                    <div
                      className={`mt-0.5 w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                        formData.is_organization_request
                          ? "bg-primary border-primary text-primary-foreground"
                          : "border-muted-foreground/50"
                      }`}
                    >
                      {formData.is_organization_request && <BadgeCheck className="w-3.5 h-3.5" />}
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-sm">Reprezentuję tę organizację</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        Zaznacz jeśli jesteś pracownikiem lub wolontariuszem tego schroniska. W celu weryfikacji
                        skontaktujemy się bezpośrednio z organizacją, korzystając z oficjalnych danych kontaktowych
                        widocznych na stronie (np. adresu email), aby potwierdzić zgłoszenie.
                      </p>
                      <p className="text-xs text-accent-foreground bg-accent dark:bg-accent/30 px-2 py-1.5 rounded mt-2 inline-flex items-center gap-1">
                        <BadgeCheck className="w-3.5 h-3.5" />
                        Po potwierdzeniu schronisko otrzyma oznaczenie „Zweryfikowane przez organizację"
                      </p>
                    </div>
                  </div>

                  <Button type="submit" className="w-full" disabled={isSubmitting}>
                    {isSubmitting ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : (
                      <Send className="w-4 h-4 mr-2" />
                    )}
                    Wyślij aktualizację
                  </Button>
                </>
              )}
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default QuickUpdatePage;
